import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ArrowLeft, Plus, X, Search, Star } from 'lucide-react';
import { toast } from 'sonner';

export default function FeaturedManagement() {
  const navigate = useNavigate();
  const [featuredArtists, setFeaturedArtists] = useState([]);
  const [allArtists, setAllArtists] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [featuredRes, artistsRes] = await Promise.all([
        apiClient.get('/featured-artists'),
        apiClient.get('/artists?status=approved')
      ]);
      setFeaturedArtists(featuredRes.data);
      setAllArtists(artistsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFeatured = async (artistId) => {
    try {
      const order = featuredArtists.length + 1;
      await apiClient.post(`/admin/featured-artists?artist_id=${artistId}&order=${order}`);
      fetchData();
    } catch (error) {
      console.error('Error adding featured artist:', error);
      toast.error('Failed to add artist to featured list');
    }
  };

  const handleRemoveFeatured = async (artistId) => {
    try {
      await apiClient.delete(`/admin/featured-artists/${artistId}`);
      fetchData();
    } catch (error) {
      console.error('Error removing featured artist:', error);
      toast.error('Failed to remove artist from featured list');
    }
  };

  const filteredArtists = allArtists.filter(
    (artist) =>
      !featuredArtists.find((f) => f.artist_id === artist.artist_id) &&
      (artist.bio.toLowerCase().includes(searchTerm.toLowerCase()) ||
        artist.genres.some((g) => g.toLowerCase().includes(searchTerm.toLowerCase())))
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-violet-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative pt-20">
      <div className="container mx-auto px-4 py-8 relative z-10 max-w-7xl">

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Featured Artists */}
          <div>
            <Card className="glass-default border-white/10">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="text-yellow-400" />
                  Featured Artists ({featuredArtists.length})
                </CardTitle>
                <p className="text-sm text-white/60">These artists appear on the landing page</p>
              </CardHeader>
              <CardContent className="space-y-3">
                {featuredArtists.length === 0 ? (
                  <p className="text-center py-8 text-white/40">No featured artists yet</p>
                ) : (
                  featuredArtists.map((artist) => (
                    <div
                      key={artist.artist_id}
                      className="flex items-center gap-4 p-4 glass-light rounded-lg"
                    >
                      {artist.media_urls?.[0] && (
                        <img
                          src={artist.media_urls[0]}
                          alt="Artist"
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{artist.bio.split(' ').slice(0, 4).join(' ')}</p>
                        <p className="text-sm text-white/60">{artist.genres.join(', ')}</p>
                      </div>
                      <Button
                        data-testid={`remove-featured-${artist.artist_id}`}
                        onClick={() => handleRemoveFeatured(artist.artist_id)}
                        size="sm"
                        variant="ghost"
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <X size={18} />
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Available Artists */}
          <div>
            <Card className="glass-default border-white/10">
              <CardHeader>
                <CardTitle>Add Artists</CardTitle>
                <div className="flex gap-2 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40" size={18} />
                    <Input
                      data-testid="search-artists"
                      placeholder="Search artists..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 focus:border-violet-500 text-white"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3 max-h-[600px] overflow-y-auto">
                {filteredArtists.length === 0 ? (
                  <p className="text-center py-8 text-white/40">No artists available</p>
                ) : (
                  filteredArtists.map((artist) => (
                    <div
                      key={artist.artist_id}
                      className="flex items-center gap-4 p-4 glass-light rounded-lg hover:bg-white/10 transition"
                    >
                      {artist.media_urls?.[0] && (
                        <img
                          src={artist.media_urls[0]}
                          alt="Artist"
                          className="w-16 h-16 rounded-lg object-cover"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{artist.bio.split(' ').slice(0, 4).join(' ')}</p>
                        <p className="text-sm text-white/60">{artist.genres.join(', ')}</p>
                      </div>
                      <Button
                        data-testid={`add-featured-${artist.artist_id}`}
                        onClick={() => handleAddFeatured(artist.artist_id)}
                        size="sm"
                        className="bg-violet-600 hover:bg-violet-500"
                      >
                        <Plus size={18} className="mr-1" />
                        Add
                      </Button>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
